import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { DrawerHeaderBackLine } from './styles';
import { Button } from '@/components/Button/Button';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import {
  BASE_CREATE_ORGANIZATION_VALIDATION_SCHEMA,
  CREATE_ORGANIZATION_INITIAL_VALUE,
  CREATE_ORGANIZATION_VALIDATION_SCHEMA,
  CreateOrganizationFormValuesType,
  ORGANIZATION_USER_ROLE_OPTIONS,
  SUBSCRIPTION_PLAN_TYPE_OPTIONS,
  getCreateOrganizationPayload,
  getCreateOrganizationWithExistingUserPayload,
} from './const';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { Typography } from '@/components/Typography/Typography';
import { Box } from '@/components/Box/Box';
import { FormikInput } from '@/components/form/fields/FormikInput/FormikInput';
import { FormikSelect } from '@/components/form/fields/FormikSelect/FormikSelect';
import { FormikSwitchToggle } from '@/components/form/fields/FormikSwitchToggle/FormikSwitchToggle';
import { COLORS } from '@/styles/colors';
import { FormikPhoneInput } from '@/components/form/fields/FormikPhoneInput/FormikPhoneInput';
import { ButtonGroup } from '@/components/Button/ButtonGroup/ButtonGroup';
import { FormikPanelRadioGroup } from '@/components/form/fields/FormikPanelRadioGroup/FormikPanelRadioGroup';
import { CreateOrgAdminInput } from '../OrganizationDetails/component/CreateOrgAdminInput';
import { useEffect, useState } from 'react';
import { useAssignUserForCreateOrganization } from './useAssignUserForCreateOrganization';
import { HTTP_BAD_REQUEST_STATUS } from '@/api/const';
import { CheckPinCodeModal } from '@/bundle/shared/components/CheckPinCodeModal/CheckPinCodeModal';
import { PIN_TOKEN_EVENT_SCOPE, SUBSCRIPTION_PLAN_TYPE } from '@/const/shared';
import { createOrganization, createOrganizationWithExistingUser } from '@/bundle/_Opco/api/api';
import { addedUserAssignmentStatus, loadAddedUserDetails } from '@/bundle/_Opco/ui/OrganizationDetails/api/api';

type CreateOrganizationFormType = {
  onSuccess: () => void;
  onCancel: () => void;
};

export const CreateOrganizationForm = ({ onSuccess, onCancel }: CreateOrganizationFormType) => {
  const [assignedUserId, setAssignedUserId] = useState<null | string>(null);
  const [submittedEmailRequest, setSubmittedEmailRequest] = useState(false);
  const [isPinCodeOpen, setIsPinCodeOpen] = useState(false);
  const [pinTokenEventScope, setPinTokenEventScope] = useState(null);

  const validationSchema = assignedUserId
    ? BASE_CREATE_ORGANIZATION_VALIDATION_SCHEMA
    : CREATE_ORGANIZATION_VALIDATION_SCHEMA;

  const {
    mutate: createOrganizationMutate,
    data: createOrganizationData,
    isPending: isPendingCreateOrganization,
    reset: createOrganizationReset,
  } = useMutation({
    mutationKey: ['create_organization'],
    mutationFn: (pinToken: string) => {
      const payload = getCreateOrganizationPayload(formik.values);

      return createOrganization(payload, pinToken);
    },
    onSuccess(response) {
      if (response.error) return;

      amplitudeService.logEvent(AMPLITUDE_EVENTS.CreateOrganizationSuccess);

      onSuccess();
    },
    onSettled() {
      setPinTokenEventScope(null);
      setIsPinCodeOpen(false);
    },
  });

  const {
    mutate: createOrganizationWithExistingUserMutate,
    data: createOrganizationWithExistingUserData,
    isPending: isPendingCreateOrganizationWithExistingUser,
    reset: createOrganizationWithExistingUserReset,
  } = useMutation({
    mutationKey: ['create_organization_with_existing_user'],
    mutationFn: (pinToken: string) => {
      const payload = getCreateOrganizationWithExistingUserPayload(formik.values, assignedUserId);

      return createOrganizationWithExistingUser(payload, pinToken);
    },
    onSuccess(response) {
      if (response.error) return;

      amplitudeService.logEvent(AMPLITUDE_EVENTS.CreateOrgAdminExistingSuccess);

      onSuccess();
    },
    onSettled() {
      setPinTokenEventScope(null);
      setIsPinCodeOpen(false);
    },
  });

  const { data: addingUserStatusData } = useQuery({
    queryKey: ['adding_user_status', assignedUserId],
    queryFn: () => {
      return addedUserAssignmentStatus(assignedUserId);
    },
    enabled: !!assignedUserId,
  });

  const { data: loadUserData } = useQuery({
    queryKey: ['load_user_details', assignedUserId],
    queryFn: () => loadAddedUserDetails(assignedUserId),
    enabled: !!assignedUserId && !!addingUserStatusData?.body?.is_available_to_add,
  });

  const formik = useFormik<CreateOrganizationFormValuesType>({
    initialValues: CREATE_ORGANIZATION_INITIAL_VALUE,
    enableReinitialize: true,
    validationSchema,
    onSubmit: () => {
      const pinTokenEventScope = assignedUserId
        ? PIN_TOKEN_EVENT_SCOPE.CREATE_ORGANIZATION_WITH_MASTER_ADMIN_ADD_ROLE
        : PIN_TOKEN_EVENT_SCOPE.CREATE_ORGANIZATION_WITH_MASTER_ADMIN;

      setPinTokenEventScope(pinTokenEventScope);
      setIsPinCodeOpen(true);
    },
  });

  useEffect(() => {
    formik.setFieldValue('organization.is_kyc_allowed', true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.organization?.subscription_plan_type]);

  const checkPinCodeSuccess = (actionPinToken: string) => {
    if (assignedUserId) {
      createOrganizationWithExistingUserMutate(actionPinToken);
    } else {
      createOrganizationMutate(actionPinToken);
    }
  };

  const searchUserEmail = (userId: string) => {
    formik.setErrors({
      ...formik.errors,
      user: {},
    });
    setAssignedUserId(userId);
    setSubmittedEmailRequest(true);
  };

  const submitUserEmail = () => {
    if (!formik.touched.user?.email && formik.errors?.user?.email) {
      formik.setTouched({ ...formik.touched, user: { email: true } });
    }
  };

  const onResetData = () => {
    setAssignedUserId(null);
    setSubmittedEmailRequest(false);
    createOrganizationReset();
    createOrganizationWithExistingUserReset();
    formik.resetForm({
      values: {
        organization: formik.values.organization,
        user: CREATE_ORGANIZATION_INITIAL_VALUE.user,
      },
    });
    formik.setFieldValue('user.email', formik.values.user.email);
  };

  const apiError = createOrganizationData?.error || createOrganizationWithExistingUserData?.error;

  useAssignUserForCreateOrganization(
    loadUserData?.body,
    formik,
    onResetData,
    assignedUserId,
    submittedEmailRequest,
    addingUserStatusData?.body?.is_available_to_add,
    apiError
  );

  const hasApiError = apiError?.statusCode === HTTP_BAD_REQUEST_STATUS;
  const addingUserStatus = addingUserStatusData?.body?.code;
  const isLoading = isPendingCreateOrganization || isPendingCreateOrganizationWithExistingUser;
  const isNotAvailableToAssignUser = addingUserStatusData?.body && !addingUserStatusData?.body?.is_available_to_add;
  const planTypeValue = formik.values.organization.subscription_plan_type?.value;
  const isDisabledIdVerificationToggle = planTypeValue === SUBSCRIPTION_PLAN_TYPE.STANDARD;
  const isDisabledUserForm = !!assignedUserId || !submittedEmailRequest || hasApiError;
  const isDisabledSave = isNotAvailableToAssignUser || !submittedEmailRequest || hasApiError;
  const formError = getResponseError(apiError);

  return (
    <>
      <FormikForm value={formik} error={formError}>
        <DrawerHeaderBackLine>
          <Typography variant='subTitleSmall' fontWeight='medium'>
            Organization Info
          </Typography>
        </DrawerHeaderBackLine>
        <Box marginTop='20px'>
          <FormikInput
            name='organization.name'
            label='Organization Name*'
            placeholder='Enter Organization Name'
            apiError={apiError}
          />
          <FormikSelect
            name='organization.subscription_plan_type'
            label='Plan Type*'
            placeholder='Select Plan Type'
            options={SUBSCRIPTION_PLAN_TYPE_OPTIONS}
            apiError={apiError}
          />

          <FormikSwitchToggle
            size='small'
            name='organization.is_kyc_allowed'
            label={
              <Typography variant='bodySmall' fontWeight='medium'>
                Enable ID Verification
              </Typography>
            }
            apiError={apiError}
            hint={
              <Typography variant='bodySmall' color={COLORS.grey[650]}>
                Requires users to complete ID verification for enhanced security.
              </Typography>
            }
            disabled={isDisabledIdVerificationToggle}
          />
          <FormikSwitchToggle
            size='small'
            name='organization.is_api_keys_allowed'
            label={
              <Typography variant='bodySmall' fontWeight='medium'>
                Enable API Management
              </Typography>
            }
            apiError={apiError}
            hint={
              <Typography variant='bodySmall' color={COLORS.grey[650]}>
                Allows this organization to access API management capabilities.
              </Typography>
            }
          />
        </Box>
        <DrawerHeaderBackLine>
          <Typography variant='subTitleSmall' fontWeight='medium'>
            User Info
          </Typography>
        </DrawerHeaderBackLine>
        <Box marginTop='20px'>
          <FormikPanelRadioGroup name='user.role' label='Role*' options={ORGANIZATION_USER_ROLE_OPTIONS} disabled />
        </Box>
        <CreateOrgAdminInput
          name='user.email'
          email={formik.values.user?.email}
          assignmentStatus={addingUserStatus}
          onSearch={searchUserEmail}
          onSubmit={submitUserEmail}
          error={formik.errors?.user?.email}
          apiError={apiError}
        />
        <FormikPhoneInput
          name='user.mobile_phone'
          label='Mobile Phone Number*'
          placeholder='Enter Mobile Phone Number With Country Code'
          disabled={isDisabledUserForm}
          apiError={apiError}
        />
        <FormikInput
          name='user.first_name'
          label='First Name*'
          placeholder='Enter First Name'
          disabled={isDisabledUserForm}
          apiError={apiError}
        />
        <FormikInput
          name='user.middle_name'
          label='Middle Name'
          placeholder='Enter Middle Name'
          disabled={isDisabledUserForm}
          apiError={apiError}
        />
        <FormikInput
          name='user.last_name'
          label='Last Name*'
          placeholder='Enter Last Name'
          disabled={isDisabledUserForm}
          apiError={apiError}
        />
        <ButtonGroup marginTop='12px'>
          <Button type='button' variant='secondary' onClick={onCancel}>
            Cancel
          </Button>
          <Button type='submit' disabled={isDisabledSave} isLoading={isLoading}>
            Save
          </Button>
        </ButtonGroup>
      </FormikForm>

      {isPinCodeOpen && (
        <CheckPinCodeModal
          isOpen={isPinCodeOpen}
          eventScope={pinTokenEventScope}
          onSuccess={checkPinCodeSuccess}
          onClose={() => setIsPinCodeOpen(false)}
        />
      )}
    </>
  );
};
